import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism'

import styles from '../../Products.module.scss'

import {
  Check as CheckIcon,
  Xml as CodeIcon,
  HelpCircle as HelpIcon,
  Document as DocumentIcon,
} from '../../../../icons'
import ResultBlock from 'components/elements/blocks/ResultBlock'
import TabsList from 'components/elements/tabs/TabsList'
import CopyButton from 'components/elements/buttons/CopyButton'
import CustomButton from 'components/elements/buttons/CustomButton'

import {
  UnblockerCommandTypes,
  UnblockerCodeExamples,
} from 'utils/unblocker/unblockerConstants'

import { selectScrapperApiKey } from 'store/user/selectors'

interface IUnblockerResultProps {
  selectedCountry: string
  websiteUrl: string
  customProxy: string
  selectedProxyType: string
  isMarkdownParamActive: boolean
  isBodyParamActive: boolean
  isXhrParamActive: boolean
  isScreenshotParamActive: boolean
  isCookiesParamActive: boolean
}

const UnblockerResult: React.FC<IUnblockerResultProps> = (props) => {
  const {
    selectedCountry,
    websiteUrl,
    customProxy,
    selectedProxyType,
    isMarkdownParamActive,
    isBodyParamActive,
    isXhrParamActive,
    isScreenshotParamActive,
    isCookiesParamActive,
  } = props

  const [selectedCommandTypeIndex, setCommandTypeIndex] = useState(0)

  const scrapperApiKey = useSelector(selectScrapperApiKey)

  const handleCodeTypeChange = (index: number) => {
    setCommandTypeIndex(index)
  }

  const commandTypeValue = UnblockerCommandTypes[selectedCommandTypeIndex].value

  const getCurrentCodeExample = () => {
    // @ts-ignore
    return UnblockerCodeExamples[commandTypeValue]
  }

  const currentCodeExample = getCurrentCodeExample()

  const outputOptions = {
    markdown: isMarkdownParamActive,
    body: isBodyParamActive,
    xhr: isXhrParamActive,
    screenshot: isScreenshotParamActive,
    cookies: isCookiesParamActive,
  }

  const getCodeString = () => {
    let codeString = currentCodeExample

    const proxySettings =
      selectedProxyType === 'custom'
        ? `{ \"proxy\": \"${customProxy}\" }`
        : `{\"country\": "${selectedCountry}", \"type\": ${selectedProxyType === 'wifi' ? 1 : 2} }`

    let options: string[] = []

    for (const [optionKey, optionValue] of Object.entries(outputOptions)) {
      if (optionValue) {
        options.push(`\"${optionKey}\": ${optionValue}`)
      }
    }

    const response = options.length ? `, \"response\": {${options.join(', ')}}` : ''

    codeString = codeString
      .replace('{{proxy_settings}}', proxySettings)
      .replace(
        '{{screenshot_output}}',
        commandTypeValue === 'curl' && isScreenshotParamActive
          ? ' --output screenshot.png'
          : ''
      )
      .replace('{{response}}', response)
      .replace('{{url}}', websiteUrl)
      .replace('{{X-SOAX-API-Secret}}', scrapperApiKey)

    return codeString
  }

  const codeString = getCodeString()

  const activeLanguage =
    commandTypeValue === 'curl' || commandTypeValue === 'node'
      ? 'javascript'
      : commandTypeValue

  return (
    <div>
      <div
        className={`
          ${styles.blockBackgroundHeader}
        `}
      >
        <div>
          <div className={styles.headerIconWrapper}>
            <CodeIcon />
          </div>

          <span className={styles.blockTitle}>Output</span>
        </div>

        <div className={styles.headerCheck}>
          <div className={styles.checkIconWrapper}>
            <CheckIcon />
          </div>
          Updated
        </div>
      </div>

      <ResultBlock
        slot={
          <div className={styles.resultBlockTabsWrapper}>
            <TabsList
              items={UnblockerCommandTypes}
              itemClassName={styles.resultBlockTabItem}
              onChange={handleCodeTypeChange}
              activeTabIndex={selectedCommandTypeIndex}
            />
          </div>
        }
        className={styles.resultBlock}
        contentClassName={styles.unblockerResultBlock}
      >
        <div className={styles.unblockerResultBlockContent}>
          <SyntaxHighlighter language={activeLanguage} style={atomDark}>
            {codeString}
          </SyntaxHighlighter>
        </div>
      </ResultBlock>

      <div className={styles.controls}>
        <CopyButton text={codeString} className={styles.rowBtn}>
          Copy code
        </CopyButton>

        {/*<CustomButton className={styles.rowBtn} color={'outline'}>
          Run in browser
        </CustomButton>*/}

        <div
          className="postman-run-button"
          data-postman-action="collection/fork"
          data-postman-visibility="public"
          data-postman-var-1="31421915-f3b68b76-6240-43b2-b821-3fa2c463d60e"
          data-postman-collection-url="entityId=31421915-f3b68b76-6240-43b2-b821-3fa2c463d60e&entityType=collection&workspaceId=5aace3ef-d96c-4abe-8bac-7c24984f933f"
        />

        <a
          href="https://app.getpostman.com/run-collection/31421915-f3b68b76-6240-43b2-b821-3fa2c463d60e?action=collection%2Ffork&source=rip_markdown&collection-url=entityId%3D31421915-f3b68b76-6240-43b2-b821-3fa2c463d60e%26entityType%3Dcollection%26workspaceId%3D5aace3ef-d96c-4abe-8bac-7c24984f933f"
          target="_blank"
        >
          <CustomButton className={styles.rowBtn} color={'outline'}>
            Run in Postman
          </CustomButton>
        </a>
      </div>

      <a
        className={styles.guideLink}
        href="https://docs.soax.com/#b779ee30-4778-4dec-9eb1-a41591f3530d"
        target={'_blank'}
        rel={'noopener noreferrer'}
      >
        <div className={styles.bookIconWrapper}>
          <HelpIcon />
        </div>{' '}
        <span>Web Data API documentation - Read in Postman</span>
      </a>

      <a
        className={styles.guideLink}
        href="https://soax.com/web-data-api"
        target={'_blank'}
        rel={'noopener noreferrer'}
      >
        <div className={styles.bookIconWrapper}>
          <DocumentIcon />
        </div>{' '}
        <span>Web Data API product page - Read more</span>
      </a>
    </div>
  )
}

export default UnblockerResult
